import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { API_URL } from "./constants";
import "./LeaderboardTab.css";

// Captain-or-above. Shows cumulative wins per voter across socialEvents
// in the chosen date range. Backend aggregates from the wins maps we
// fill in via the RSVPs tab + the GroupMe member roster for nicknames.
//
// "Events" counts events where the user had at least one win recorded
// (a proxy for "played and was tracked") — once we capture full
// attendance lists we can break this into played/attended distinctly.
const RANGES = [
  { id: "week", label: "Last 7 days", pastDays: 7 },
  { id: "month", label: "Last 30 days", pastDays: 30 },
  { id: "quarter", label: "Last 90 days", pastDays: 90 },
  { id: "year", label: "Last 365 days", pastDays: 365 },
];

const LeaderboardTab = () => {
  const { getAuthHeaders } = useAuth();
  const [rangeId, setRangeId] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(
    async (pastDays) => {
      setLoading(true);
      setError("");
      try {
        const headers = getAuthHeaders();
        const resp = await fetch(
          `${API_URL}?type=leaderboard&pastDays=${encodeURIComponent(pastDays)}`,
          { method: "GET", headers }
        );
        const body = await resp.json();
        if (!resp.ok) {
          throw new Error(body?.error || body?.message || `HTTP ${resp.status}`);
        }
        setData(body);
      } catch (err) {
        setError(err.message || "Failed to load leaderboard");
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [getAuthHeaders]
  );

  useEffect(() => {
    const range = RANGES.find((r) => r.id === rangeId) || RANGES[1];
    load(range.pastDays);
  }, [rangeId, load]);

  const range = RANGES.find((r) => r.id === rangeId) || RANGES[1];

  return (
    <div className="leaderboard-tab">
      <h2 className="leaderboard-tab__title">Leaderboard</h2>

      <div className="leaderboard-tab__controls">
        <label htmlFor="leaderboard-range-select">Range:</label>
        <select
          id="leaderboard-range-select"
          value={rangeId}
          onChange={(e) => setRangeId(e.target.value)}
        >
          {RANGES.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="leaderboard-tab__btn"
          onClick={() => load(range.pastDays)}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {loading && (
        <div className="leaderboard-tab__info">Loading…</div>
      )}
      {error && (
        <div className="leaderboard-tab__error">Error: {error}</div>
      )}

      {data && !loading && (
        <>
          <div className="leaderboard-tab__meta">
            {data.rangeStart} → {data.rangeEnd} · {data.eventsScanned} events
            scanned · {data.eventsWithWins} with wins recorded
          </div>

          {data.leaderboard.length === 0 ? (
            <div className="leaderboard-tab__info">
              No wins recorded in this range yet. Add some on the RSVPs tab.
            </div>
          ) : (
            <table className="leaderboard-tab__table">
              <thead>
                <tr>
                  <th className="leaderboard-tab__rank-col">#</th>
                  <th>Player</th>
                  <th className="leaderboard-tab__num-col">Wins</th>
                  <th className="leaderboard-tab__num-col">Events</th>
                </tr>
              </thead>
              <tbody>
                {data.leaderboard.map((row, idx) => (
                  <tr key={row.userId}>
                    <td className="leaderboard-tab__rank-col">{idx + 1}</td>
                    <td>{row.name}</td>
                    <td className="leaderboard-tab__num-col">
                      {row.totalWins}
                    </td>
                    <td className="leaderboard-tab__num-col">
                      {row.eventsWithWins}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default LeaderboardTab;
